import React, { useState, useMemo } from 'react';
import { Alert, Typography, Link } from '@one-thd/sui-atomic-components';
import { routeBuilder } from '../../../utils/general';
import { putAccountIdValueInSessionStorage } from '../../../utils/shared/accountIdValueStorage';
import { IOrderDetailsResponse } from '../../../models/order-details';
import { isBopisOutOfStockRecoverable } from '../../order-details/util/FulfillmentStatus';
import { pushErrorEvent } from '../../../utils/analytics';
import { useTrackOncePerKey } from '../../../hooks/useTrackOnce';

export interface BopisOutOfStockRecoveryAlertProps {
  allOrderDetails: IOrderDetailsResponse[] | [];
}

const getOrderNumber = (orderDetailsData: IOrderDetailsResponse) =>
  orderDetailsData?.orderNumber || orderDetailsData?.orderNumbers?.[0];

// Single source of truth for the order-number label so the user-facing link and
// the analytics message stay in sync (Figma renders this with no space after "Order").
const orderNumberLabel = (orderNumber: string | undefined) =>
  orderNumber ? `Order#${orderNumber}` : '';

export const BopisOutOfStockRecoveryAlert = ({
  allOrderDetails,
}: BopisOutOfStockRecoveryAlertProps) => {
  const [showAlert, setShowAlert] = useState(true);

  /* This function filters unique BOPIS orders where at least one fulfillmentGroup has a cancellationStatus of 'OUT_OF_STOCK', 
  isOosRecoveryEnabled set to true in actions, and a non-empty oosRecoveryOptions array. */
  const bopisOutOfStockRecoverableOrders = useMemo(() => {
    const uniqueOrderNumbers = new Set();
    return allOrderDetails?.filter((order) => {
      const orderNumber = getOrderNumber(order);
      if (!orderNumber || uniqueOrderNumbers.has(orderNumber)) {
        return false;
      }
      const hasBopisRecoverable = order?.fulfillmentGroups?.some(
        (fulfillmentGroup) =>
          fulfillmentGroup?.fulfillmentType === 'BuyOnlinePickupInStore' &&
          isBopisOutOfStockRecoverable(fulfillmentGroup),
      );
      if (hasBopisRecoverable) {
        uniqueOrderNumbers.add(orderNumber);
        return true;
      }
      return false;
    });
  }, [allOrderDetails]);

  const alertMessage = useMemo(() => {
    const orderNumbers = bopisOutOfStockRecoverableOrders
      .map(getOrderNumber)
      .filter(Boolean)
      .map((orderNumber) => orderNumberLabel(orderNumber))
      .join(', ');

    return `Some of your items are out of stock. Review your options for ${orderNumbers}`;
  }, [bopisOutOfStockRecoverableOrders]);

  // Track once per distinct message so a changed recoverable-order set re-fires.
  useTrackOncePerKey(
    showAlert && bopisOutOfStockRecoverableOrders?.length ? alertMessage : null,
    () =>
      pushErrorEvent(
        'purchase history',
        'n/a',
        alertMessage,
        'inline',
        'advisory',
      ),
  );

  const renderOrderLinks = (
    outOfStockRecoverableOrders: IOrderDetailsResponse[],
  ) => {
    return outOfStockRecoverableOrders.map(
      (orderDetailsData, index: number) => {
        const orderNumber = getOrderNumber(orderDetailsData);
        const orderDetailsUrl = `/myaccount/order-details${routeBuilder({
          orderNumber,
          salesDate: orderDetailsData?.salesDate,
          storeNumber: orderDetailsData?.storeNumber,
          transactionId: orderDetailsData?.transactionId,
          orderOrigin: orderDetailsData?.orderOrigin,
          registerNumber: orderDetailsData?.registerNumber,
          nickName: orderDetailsData?.nickName,
          POJobName: orderDetailsData?.POJobName,
        })}`;
        const orderDetailsOrderNumber =
          new URLSearchParams(orderDetailsUrl.split('?')[1]).get(
            'orderNumber',
          ) ?? '';
        return (
          <span key={`out-of-stock-recoverable-order-${index}`}>
            <Link
              href={orderDetailsUrl}
              variant="body-sm"
              onClick={() =>
                putAccountIdValueInSessionStorage(
                  orderDetailsOrderNumber,
                  orderDetailsData?.accountIdValue,
                  orderDetailsData?.accountIdType,
                )
              }
            >
              {orderNumberLabel(orderNumber)}
            </Link>
            {index < outOfStockRecoverableOrders.length - 1 && ', '}
          </span>
        );
      },
    );
  };

  if (!bopisOutOfStockRecoverableOrders?.length || !showAlert) return null;

  return (
    <Alert
      status="warning"
      size="large"
      headline="Some of your items are out of stock."
      onClose={() => {
        setShowAlert(false);
      }}
    >
      <Typography variant="body-sm">
        Review your options for{' '}
        {renderOrderLinks(bopisOutOfStockRecoverableOrders)}
      </Typography>
    </Alert>
  );
};
